import { useCallback } from "react";
import { toast } from "react-toastify";
import { isAxiosError } from "axios";
import {
  type OwnerRelatedRegistriesQuery,
  type OwnerRelatedRegistryResponse,
} from "@/types/ownerRelatedRegistries";
import { useOwnerRelatedRegistriesMutation } from "../api/OwnerRelatedRegistriesAPI";

interface UseOwnerRelatedRegistriesReturn {
  search: (
    params: OwnerRelatedRegistriesQuery
  ) => Promise<OwnerRelatedRegistryResponse[]>;
  reset: () => void;
  data: OwnerRelatedRegistryResponse[];
  isLoading: boolean;
}

export const useOwnerRelatedRegistries =
  (): UseOwnerRelatedRegistriesReturn => {
    const { trigger, reset, data, isMutating } =
      useOwnerRelatedRegistriesMutation();

    const handleError = useCallback((error: unknown) => {
      if (isAxiosError(error)) {
        if (error.response?.status === 400) {
          const message =
            typeof error.response.data === "object" &&
            error.response.data !== null &&
            "detail" in error.response.data
              ? String((error.response.data as { detail: unknown }).detail)
              : "検索条件を確認してください";
          toast.error(message);
          return;
        }
      }
      toast.error("関連不動産の検索に失敗しました");
    }, []);

    const search = useCallback(
      async (params: OwnerRelatedRegistriesQuery) => {
        try {
          const response = await trigger(params);
          if (!response || response.length === 0) {
            return [];
          }
          return response;
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 404) {
            return [];
          }
          handleError(error);
          throw error;
        }
      },
      [trigger, handleError]
    );

    const safeReset = useCallback(() => {
      reset();
    }, [reset]);

    return {
      search,
      reset: safeReset,
      data: data ?? [],
      isLoading: isMutating,
    };
  };
